import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Plus, Trash2, RefreshCw, Wifi, WifiOff, QrCode,
  Smartphone, AlertCircle, CheckCircle2, X, Loader, Star,
} from 'lucide-react';
import api from '../../services/api';

const STATUS_INFO = {
  open:       { label: 'Conectado',      color: 'bg-green-100 text-green-700', icon: Wifi },
  connecting: { label: 'Conectando...',  color: 'bg-yellow-100 text-yellow-700', icon: Loader },
  close:      { label: 'Desconectado',   color: 'bg-gray-100 text-gray-500', icon: WifiOff },
};

function InstanceCard({ instance, onDelete, onDisconnect, onRefresh, padrao, onDefinirPadrao }) {
  const [showQR, setShowQR] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState('');
  const pollRef = useRef(null);
  const qc = useQueryClient();

  const status = instance.connectionStatus || 'close';
  const cfg = STATUS_INFO[status] || STATUS_INFO.close;
  const Icon = cfg.icon;

  const loadQR = async () => {
    setQrLoading(true);
    setQrError('');
    try {
      const res = await api.get(`/whatsapp/${instance.name}/qr`);
      setQrData(res.data.data);
    } catch (err) {
      setQrError(err.response?.data?.error || err.message);
    } finally {
      setQrLoading(false);
    }
  };

  const handleShowQR = () => {
    setShowQR(true);
    loadQR();
    // Poll connection every 3s to detect when phone scans QR
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/whatsapp/${instance.name}/status`);
        const state = res.data.data?.instance?.state;
        if (state === 'open') {
          clearInterval(pollRef.current);
          setShowQR(false);
          qc.invalidateQueries(['whatsapp-instancias']);
        } else if (state !== 'connecting') {
          // Refresh QR if disconnected again
          loadQR();
        }
      } catch {}
    }, 3000);
  };

  const handleHideQR = () => {
    clearInterval(pollRef.current);
    setShowQR(false);
    setQrData(null);
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  return (
    <div className="bg-white rounded-xl shadow p-5 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
            {instance.profilePicUrl ? (
              <img src={instance.profilePicUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <Smartphone size={20} className="text-gray-400" />
            )}
          </div>
          <div>
            <div className="font-semibold text-gray-800 text-sm">{instance.name}</div>
            {instance.profileName && (
              <div className="text-xs text-gray-500">{instance.profileName}</div>
            )}
            {instance.ownerJid && (
              <div className="text-xs text-gray-400 font-mono">
                +{instance.ownerJid.replace('@s.whatsapp.net', '').replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '$1 ($2) $3-$4')}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.color}`}>
            <Icon size={11} className={status === 'connecting' ? 'animate-spin' : ''} />
            {cfg.label}
          </span>
          {padrao ? (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-600">
              <Star size={11} fill="currentColor" /> Padrão de envio
            </span>
          ) : status === 'open' && (
            <button onClick={() => onDefinirPadrao(instance.name)} className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-amber-600">
              <Star size={11} /> Definir como padrão
            </button>
          )}
        </div>
      </div>

      {/* QR Code section */}
      {showQR && (
        <div className="border border-dashed border-gray-300 rounded-xl p-4 text-center space-y-2">
          {qrLoading ? (
            <div className="flex items-center justify-center gap-2 text-gray-400 text-sm py-4">
              <Loader size={16} className="animate-spin" /> Gerando QR Code...
            </div>
          ) : qrError ? (
            <div className="text-red-500 text-sm">{qrError}</div>
          ) : qrData?.base64 ? (
            <>
              <img src={qrData.base64} alt="QR Code" className="mx-auto w-48 h-48 rounded-lg" />
              <p className="text-xs text-gray-500">Abra o WhatsApp no celular → Menu → Aparelhos conectados → Conectar aparelho</p>
              <p className="text-xs text-blue-500 animate-pulse">Aguardando leitura... (atualiza automático)</p>
            </>
          ) : (
            <div className="text-gray-400 text-sm py-4">QR Code não disponível</div>
          )}
          <button onClick={() => { loadQR(); }} className="text-xs text-blue-500 hover:underline">
            Atualizar QR
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        {status !== 'open' ? (
          <button
            onClick={showQR ? handleHideQR : handleShowQR}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700"
          >
            <QrCode size={12} /> {showQR ? 'Fechar QR' : 'Conectar via QR'}
          </button>
        ) : (
          <button
            onClick={() => onDisconnect(instance.name)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg text-xs font-medium hover:bg-yellow-100"
          >
            <WifiOff size={12} /> Desconectar
          </button>
        )}
        <button
          onClick={() => onRefresh(instance.name)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200"
        >
          <RefreshCw size={12} /> Status
        </button>
        <button
          onClick={() => onDelete(instance.name)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 ml-auto"
        >
          <Trash2 size={12} /> Remover
        </button>
      </div>
    </div>
  );
}

export default function WhatsAppManager() {
  const qc = useQueryClient();
  const [novoNome, setNovoNome] = useState('');
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['whatsapp-instancias'],
    queryFn: () => api.get('/whatsapp').then(r => r.data),
    refetchInterval: 10000,
  });

  const { data: configData } = useQuery({
    queryKey: ['whatsapp-config'],
    queryFn: () => api.get('/whatsapp/config').then(r => r.data.data),
  });
  const instanciaPadrao = configData?.instancia_padrao || null;

  const definirPadraoMut = useMutation({
    mutationFn: (nome) => api.put('/whatsapp/config', { instancia_padrao: nome }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp-config'] }),
  });

  const instancias = data?.data || [];

  const handleCriar = async () => {
    if (!novoNome.trim()) return;
    setCriando(true);
    setErro('');
    try {
      await api.post('/whatsapp', { nome: novoNome.trim() });
      setSucesso(`Instância "${novoNome}" criada! Escaneie o QR Code para conectar.`);
      setNovoNome('');
      qc.invalidateQueries(['whatsapp-instancias']);
      setTimeout(() => setSucesso(''), 5000);
    } catch (err) {
      setErro(err.response?.data?.error || err.message);
    } finally {
      setCriando(false);
    }
  };

  const handleDesconectar = async (name) => {
    if (!confirm(`Desconectar "${name}"?`)) return;
    try {
      await api.post(`/whatsapp/${name}/desconectar`);
      qc.invalidateQueries(['whatsapp-instancias']);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleDeletar = async (name) => {
    if (!confirm(`Remover permanentemente "${name}"? Isso desconecta o número.`)) return;
    try {
      await api.delete(`/whatsapp/${name}`);
      qc.invalidateQueries(['whatsapp-instancias']);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleRefreshStatus = async (name) => {
    try {
      await api.get(`/whatsapp/${name}/status`);
      qc.invalidateQueries(['whatsapp-instancias']);
    } catch {}
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">WhatsApp Conectado</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gerencie instâncias WhatsApp via Evolution API</p>
        </div>
        <button onClick={() => refetch()} className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-50">
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      {erro && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          <AlertCircle size={15} /> {erro}
          <button onClick={() => setErro('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}
      {sucesso && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-xl">
          <CheckCircle2 size={15} /> {sucesso}
        </div>
      )}

      {/* Nova instância */}
      <div className="bg-white rounded-xl shadow p-5">
        <h2 className="font-semibold text-gray-700 text-sm mb-3">Nova Instância WhatsApp</h2>
        <div className="flex gap-3">
          <input
            value={novoNome}
            onChange={e => setNovoNome(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCriar()}
            placeholder="Ex: Corretor João Silva, Atendimento Comercial..."
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button
            onClick={handleCriar}
            disabled={criando || !novoNome.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-secondary disabled:opacity-50"
          >
            <Plus size={15} /> {criando ? 'Criando...' : 'Criar'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Após criar, escaneie o QR Code com o celular do corretor para vincular o número.
        </p>
      </div>

      {/* Instâncias */}
      {isLoading ? (
        <div className="py-10 text-center text-gray-400">Carregando instâncias...</div>
      ) : instancias.length === 0 ? (
        <div className="bg-white rounded-xl shadow py-14 text-center text-gray-400 space-y-2">
          <Smartphone size={32} className="mx-auto opacity-30" />
          <p className="text-sm">Nenhuma instância criada ainda.</p>
          <p className="text-xs">Crie uma acima e conecte um número de WhatsApp.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {instancias.map(inst => (
            <InstanceCard
              key={inst.name}
              instance={inst}
              onDelete={handleDeletar}
              onDisconnect={handleDesconectar}
              onRefresh={handleRefreshStatus}
              padrao={inst.name === instanciaPadrao}
              onDefinirPadrao={(nome) => definirPadraoMut.mutate(nome)}
            />
          ))}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-xs text-blue-700 space-y-1">
        <p className="font-semibold">Como funciona:</p>
        <p>1. Crie uma instância com o nome do corretor.</p>
        <p>2. Clique em "Conectar via QR" e mostre o código para o corretor escanear com o celular.</p>
        <p>3. O status muda para "Conectado" automaticamente após a leitura.</p>
        <p>4. A partir daí, essa instância pode ser usada nos Envios em Massa.</p>
        <p>5. Clique na estrela de uma instância conectada pra defini-la como padrão — é ela que vai tentar enviar primeiro mensagens automáticas (ex: confirmação de agendamento de evento com QR Code).</p>
      </div>
    </div>
  );
}
